"use client"
import { useEffect, useState, useTransition } from "react"
import { useParams, useRouter } from "next/navigation"
import { getNormalJobDetails } from "@/actions/jobs"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
	MapPin,
	Clock,
	Users,
	Calendar,
	Target,
	ArrowLeft,
	UserCheck,
	Shield,
} from "lucide-react"
import { useTranslations } from "next-intl"
import { applyToJob } from "@/actions/jobs"
import { toast } from "sonner"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { useAuth } from "@/components/auth-provider"
import useActionError, { HandleActionError } from "@/hooks/use-action-error"

export default function JobDetailsPage() {
	const params = useParams()
	const router = useRouter()
	const t = useTranslations("home.jobs.details")
	const jobListingT = useTranslations("home.jobListing")
	const [job, setJob] = useState<{
		job: any
		organization: any
		user: any
	} | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [isPending, startTransition] = useTransition()
	const enums = useTranslations("cities")
	const user = useAuth()
	const handleActionError: HandleActionError = useActionError()

	const fetchJobDetails = async () => {
		try {
			const result = await getNormalJobDetails({
				jobId: params.id as string,
			})
			handleActionError(result)
			if (result.data) {
				setJob(result.data)
			}
		} finally {
			setIsLoading(false)
		}
	}

	useEffect(() => {
		if (params.id) {
			fetchJobDetails()
		}
	}, [params.id])

	const handleApply = () => {
		startTransition(async () => {
			try {
				const result = await applyToJob({ jobId: params.id as string })
				handleActionError(result, {
					onSuccess() {
						toast.success(
							jobListingT("messages.applicationSuccess")
						)
					},
				})
			} catch {}
		})
		setIsDialogOpen(false)
	}

	const getWorkTimeText = (time: string) => {
		if (time === "morning") return jobListingT("timings.morning")
		if (time === "evening") return jobListingT("timings.evening")
		return jobListingT("timings.specific")
	}

	if (isLoading) {
		return (
			<div className="container mx-auto px-4 py-8">
				{/* Back Button Skeleton */}
				<div className="mb-6">
					<div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
				</div>

				{/* Header Skeleton */}
				<div className="mb-6">
					<div className="flex flex-wrap items-center justify-between gap-4 mb-4">
						<div className="flex-1 space-y-3">
							<div className="h-10 bg-gray-200 rounded w-3/4 animate-pulse"></div>
							<div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse"></div>
						</div>
						<div className="h-7 bg-gray-200 rounded-full w-24 animate-pulse"></div>
					</div>
				</div>

				{/* Header Skeleton */}
				<div className="mb-8">
					<div className="h-10 bg-gray-200 rounded w-3/4 mb-4 animate-pulse"></div>
					<div className="flex items-center gap-2">
						<div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
						<div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
					</div>
				</div>

				<div className="space-y-6">
					{/* Job Details Grid Skeleton */}
					<Card className="animate-pulse">
						<CardContent className="p-6">
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
								{Array.from({ length: 6 }).map((_, i) => (
									<div
										key={i}
										className="flex items-center gap-3"
									>
										<div className="h-5 w-5 bg-gray-200 rounded"></div>
										<div className="space-y-2 flex-1">
											<div className="h-4 bg-gray-200 rounded w-24"></div>
											<div className="h-6 bg-gray-200 rounded w-20"></div>
										</div>
									</div>
								))}
							</div>
						</CardContent>
					</Card>

					{/* Salary Card Skeleton */}
					<Card className="bg-gradient-to-br from-lochmara-50 to-lochmara-100 animate-pulse">
						<CardHeader>
							<div className="h-6 bg-gray-200 rounded w-32"></div>
						</CardHeader>
						<CardContent>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{Array.from({ length: 2 }).map((_, i) => (
									<div
										key={i}
										className="p-4 bg-white rounded-lg shadow-sm space-y-2"
									>
										<div className="h-4 bg-gray-200 rounded w-24 mx-auto"></div>
										<div className="h-8 bg-gray-200 rounded w-32 mx-auto"></div>
									</div>
								))}
							</div>
						</CardContent>
					</Card>

					{/* Job Description Card Skeleton */}
					<Card className="animate-pulse">
						<CardHeader>
							<div className="h-6 bg-gray-200 rounded w-32"></div>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="h-4 bg-gray-200 rounded w-full"></div>
							<div className="h-4 bg-gray-200 rounded w-full"></div>
							<div className="h-4 bg-gray-200 rounded w-5/6"></div>
						</CardContent>
					</Card>

					{/* Requirements Card Skeleton */}
					<Card className="animate-pulse">
						<CardHeader>
							<div className="h-6 bg-gray-200 rounded w-40"></div>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="h-4 bg-gray-200 rounded w-full"></div>
							<div className="h-4 bg-gray-200 rounded w-full"></div>
							<div className="h-4 bg-gray-200 rounded w-4/5"></div>
						</CardContent>
					</Card>

					{/* Apply Button Skeleton */}
					<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
				</div>
			</div>
		)
	}

	if (!job) {
		return (
			<div className="container mx-auto px-4 py-8">
				<Card className="text-center py-12">
					<CardContent>
						<h2 className="text-2xl font-bold text-gray-900 mb-2">
							{t("notFound.title")}
						</h2>
						<p className="text-gray-600 mb-4">
							{t("notFound.description")}
						</p>
						<Button onClick={() => router.push("/jobs")}>
							{t("notFound.backToJobs")}
						</Button>
					</CardContent>
				</Card>
			</div>
		)
	}

	const canApply =
		job.job.status === "approved" && user && user.type === "employee"

	return (
		<>
			<div className="container mx-auto px-4 py-8 max-w-4xl">
				<Button
					variant="link"
					onClick={() => router.back()}
					className="mb-6"
				>
					<ArrowLeft className="h-4 w-4 me-2" />
					{t("actions.backToJobs")}
				</Button>

				{/* Job Title */}
				<div className="mb-8">
					<h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
						{job.job.title}
					</h1>
					<div className="flex items-center gap-2 text-gray-600">
						<MapPin className="h-5 w-5" />
						<span className="text-lg font-medium">
							{enums(job.user.city)} - {job.user.neighborhood}
						</span>
					</div>
				</div>

				<div className="space-y-6">
					{/* Job Details Grid */}
					<Card>
						<CardContent className="p-6">
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
								{/* Working Hours */}
								<div className="flex items-center gap-3">
									<Clock className="h-5 w-5 text-gray-500" />
									<div>
										<p className="text-sm text-gray-600">
											{t("workDetails.dailyHours")}
										</p>
										<p className="font-semibold text-lg">
											{job.job.hours}{" "}
											{jobListingT("units.hours")}
										</p>
									</div>
								</div>

								{/* Rest Days */}
								<div className="flex items-center gap-3">
									<Calendar className="h-5 w-5 text-gray-500" />
									<div>
										<p className="text-sm text-gray-600">
											{t("workDetails.weeklyRestDays")}
										</p>
										<p className="font-semibold text-lg">
											{job.job.weeklyRestDays}{" "}
											{jobListingT("units.restDays")}
										</p>
									</div>
								</div>

								{/* Specialization */}
								{job.job.specialization && (
									<div className="flex items-center gap-3">
										<Target className="h-5 w-5 text-gray-500" />
										<div>
											<p className="text-sm text-gray-600">
												{t("basicInfo.specialization")}
											</p>
											<p className="font-semibold text-lg">
												{job.job.specialization}
											</p>
										</div>
									</div>
								)}

								{/* Working Period */}
								<div className="flex items-center gap-3">
									<Clock className="h-5 w-5 text-gray-500" />
									<div>
										<p className="text-sm text-gray-600">
											{t("workDetails.workTimings")}
										</p>
										<p className="font-semibold text-lg">
											{getWorkTimeText(job.job.timings)}
										</p>
									</div>
								</div>

								{/* Insurance */}
								<div className="flex items-center gap-3">
									<Shield className="h-5 w-5 text-gray-500" />
									<div>
										<p className="text-sm text-gray-600">
											{t("additional.medicalInsurance")}
										</p>
										<p className="font-semibold text-lg">
											{job.job.medicalInsurance}
										</p>
									</div>
								</div>

								{/* Number of Vacancies */}
								<div className="flex items-center gap-3">
									<Users className="h-5 w-5 text-gray-500" />
									<div>
										<p className="text-sm text-gray-600">
											{t("workDetails.capacity")}
										</p>
										<p className="font-semibold text-lg">
											{job.job.capacity}
										</p>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<p className="text-lg font-bold text-lochmara-700 mb-4">
								{t("jobDescription.title")}
							</p>

							<p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
								{job.job.description}
							</p>
						</CardContent>
					</Card>

					{/* Requirements */}
					<Card>
						<CardContent>
							<p className="text-lg font-bold text-lochmara-700 mb-4">
								{t("requirements.title")}
							</p>

							<p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
								{job.job.requirements}
							</p>
						</CardContent>
					</Card>

					{/* Salary Information */}
					<Card className="bg-gradient-to-br from-lochmara-50 to-lochmara-100">
						<CardContent>
							<p className="text-lg font-bold mb-4">
								{t("financial.title")}
							</p>

							<div>
								<div className="text-center p-4 bg-gradient-to-r from-lochmara-600 to-lochmara-700 text-white rounded-lg shadow-sm">
									<p className="text-sm mb-2">
										{t("financial.totalSalary")}
									</p>
									<p className="text-2xl font-bold">
										{job.job.rawSalary.toLocaleString()}{" "}
										{jobListingT("units.currency")}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Apply Button */}
					{canApply ? (
						<Button
							size="lg"
							className="w-full"
							onClick={() => setIsDialogOpen(true)}
						>
							<UserCheck className="h-5 w-5 me-2" />
							{t("actions.apply")}
						</Button>
					) : (
						<Button size="lg" className="w-full" disabled>
							{!user
								? t("actions.cantApply")
								: t("actions.jobClosed")}
						</Button>
					)}
				</div>
			</div>

			{/* Apply Dialog */}
			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{jobListingT("dialog.title")}</DialogTitle>
						<DialogDescription className="text-start mt-4">
							{jobListingT("dialog.description")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="flex gap-2">
						<Button
							variant="destructive"
							disabled={isPending}
							onClick={() => setIsDialogOpen(false)}
						>
							{jobListingT("dialog.cancel")}
						</Button>
						<Button disabled={isPending} onClick={handleApply}>
							{isPending
								? jobListingT("dialog.submitting")
								: jobListingT("dialog.submit")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
